import { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IdentityContext from '../../contexts/IdentityContext';
import useClickOutside from '../../hooks/useClickOutside';
import Navigation from '@pelckmans/elementis/components/navigation';
import classNames from 'classnames';
import SettingsIcon from '@pelckmans/elementis/icons/settings';
import DropdownMenu from '@pelckmans/elementis/components/dropdown-menu';

export default function AdminMenu() {
  const {
    identity: { canAccessShell },
  } = useContext(IdentityContext);
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [buttonEl, setButtonEl] = useState<HTMLButtonElement | null>(null);
  const { REACT_APP_SHELL_URL, REACT_APP_RESHELL_URL } = process.env;

  const closeMenu = () => {
    setShowMenu(false);
    buttonEl?.focus();
  };

  const handleCollapse = (event?: Event) => {
    if (event?.type === 'mousedown' && buttonEl?.contains(event.target as Node)) return;

    closeMenu();
  };

  useClickOutside({
    ref,
    elementId: 'admin-menu',
    onClickOutside: () => setShowMenu(false),
    enabled: showMenu,
  });

  if (!canAccessShell) return null;

  return (
    <div ref={ref} className="page-level-menu">
      <Navigation>
        <Navigation.List>
          <li role="none">
            <button
              ref={setButtonEl}
              role="menuitem"
              onClick={() => setShowMenu(prev => !prev)}
              className={classNames('menu-navigation-item', { active: showMenu })}
              aria-label={t('APP_NAV.SIDE_NAV.ADMIN_BUTTON.A11Y_LABEL')}
              aria-haspopup="menu"
              aria-expanded={showMenu}
              aria-controls="admin-menu"
            >
              <SettingsIcon size="lg" focusable={false} aria-hidden="true" className="menu-navigation-item-icon" />
            </button>
          </li>
        </Navigation.List>
      </Navigation>

      {showMenu && (
        <DropdownMenu
          actionEl={buttonEl}
          referenceEl={ref.current}
          onCollapse={handleCollapse}
          menuProps={{
            ariaLabel: t('APP_NAV.SIDE_NAV.ADMIN_MENU.A11Y_LABEL'),
            id: 'admin-menu',
          }}
          role="menu"
          placement="right-end"
          offset={20}
          grow
          showSeparators
          disableBackgroundScroll
          selectableByEnter
        >
          <DropdownMenu.Item as="link" href={REACT_APP_SHELL_URL} target="_blank" role="menuitem" text="Shell" />
          <DropdownMenu.Item as="link" href={REACT_APP_RESHELL_URL} target="_blank" role="menuitem" text="Reshell" />
        </DropdownMenu>
      )}
    </div>
  );
}
